import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePDF } from '../../../utils/reportUtils';
import ReportDialog from '../ReportDialog';
import { getinvestigationInfo, Investigation } from '../../../pages/Registration/NewRegistration/PatientPartnerRegistration/PatientReport/reportHelper';
import InvestigationReport from './InvestigationReport';
import { usePatientContext } from '../../SubPatinetProvider';
import { Typography, Box, CircularProgress } from '@mui/material';

interface Props {
  open: boolean;
  onClose: () => void;
}

const InvestigationReportGen: React.FC<Props> = ({ open, onClose }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { patientId } = usePatientContext();
  const { t: tInvestigation } = useTranslation('investigation_config');
  const { t: tGeneral } = useTranslation('general');
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  
  // Fetch investigations for a patient with error handling using Promises
  function fetchInvestigations(patientId: string): Promise<Investigation[]> {
    return getinvestigationInfo(`patient_id=${patientId}`)
      .then((data) => {
        // eslint-disable-next-line no-console
        console.log('Fetched investigation data:', data);
        return data?.objects || [];
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch investigations:', error);
        return [];
      });
  }

  useEffect(() => {
    if (open && patientId) {
      setIsLoading(true);
      fetchInvestigations(String(patientId))
        .then((data) => {
          if (isMountedRef.current) {
            setInvestigations(data);
          }
        })
        .catch(() => {
          if (isMountedRef.current) {
            setInvestigations([]);
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        });
    } else if (!open) {
      // Reset state when dialog closes
      setInvestigations([]);
      setIsLoading(false);
    }
  }, [open, patientId]);

  const handleDownloadPDF = () => {
    if (reportRef.current) {
      generatePDF(reportRef.current, tInvestigation('InvestigationReport_title', { defaultValue: 'Investigation Report' }));
    }
  };

  const handlePrint = () => window.print();

  return (
    <ReportDialog
      open={open}
      onClose={onClose}
      title={tInvestigation('InvestigationReport_title', { defaultValue: 'Investigation Report' })}
      onDownload={!isLoading && investigations.length > 0 ? handleDownloadPDF : undefined}
      onPrint={!isLoading && investigations.length > 0 ? handlePrint : undefined}
      showActions={!isLoading && investigations.length > 0}
      downloadLabel={tGeneral('DownloadReport', { defaultValue: 'Download Report' })}
      printLabel={tGeneral('PrintReport', { defaultValue: 'Print Report' })}
    >
      <div ref={reportRef}>
        {isLoading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              {tGeneral('loadingInvestigationReport', { defaultValue: 'Loading investigation report...' })}
            </Typography>
          </Box>
        ) : investigations.length > 0 ? (
          <InvestigationReport investigations={investigations} />
        ) : (
          <Typography variant="h6" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            {tGeneral('dataNotAvailable', { defaultValue: 'Data is not available' })}
          </Typography>
        )}
      </div>
    </ReportDialog>
  );
};

export default InvestigationReportGen;
